import {
  Users,
  HeartPulse,
  GraduationCap,
  BadgeAlert,
  Scale,
  Info,
  type LucideIcon,
} from "lucide-react";
import { useWizard } from "@/contexts/WizardContext";
import { LockedBanner } from "../common/LockedBanner";
import { ProgressCard } from "../common/ProgressCard";
import { CardPanelHeader } from "../common/CardPanelHeader";
import { SectionCard } from "../common/SectionCard";
import { VoiceTextareaField } from "../common/VoiceTextareaField";
import type { StorySocialData } from "@/contexts/wizard/types";

interface StorySocialCardProps {
  onCollapse: () => void;
}

type StorySocialFieldKey = keyof StorySocialData;

type SocialSectionField = {
  key: StorySocialFieldKey;
  id: string;
  label: string;
  placeholder: string;
  rows?: number;
};

type SocialSectionConfig = {
  id: string;
  title: string;
  description: string;
  icon: LucideIcon;
  fields: SocialSectionField[];
};

export function StorySocialCard({ onCollapse }: StorySocialCardProps) {
  const {
    storySocialData: data,
    updateStorySocialData: update,
    humanRightsData,
    forceSave,
    saveStatus,
    isLocked,
    isReadOnly,
    storySocialCompletion,
  } = useWizard();

  const disabled = isLocked || isReadOnly;
  const sections: SocialSectionConfig[] = [
    {
      id: "b8",
      title: "1. B8 Workforce",
      description: "Workforce, health & safety, training, diversity, human rights.",
      icon: Users,
      fields: [
        {
          key: "b8WorkforceProfile",
          id: "story-social-b8-workforce-profile",
          label: "Describe your workforce profile and employment approach (e.g., permanent/temporary mix, key roles, seasonality, turnover context).",
          placeholder: "e.g. We employ 50 full-time staff and 10 seasonal workers during peak periods. Key roles include production, sales, and admin. Annual turnover is ~12%...",
          rows: 4,
        },
        {
          key: "b8WorkforcePriorities",
          id: "story-social-b8-workforce-priorities",
          label: "What are your main workforce priorities this year (2-3)?",
          placeholder: "e.g. 1) Reduce turnover through better onboarding. 2) Increase diversity in management roles. 3) Improve work-life balance...",
          rows: 3,
        },
        {
          key: "b8EmployeeSupportActions",
          id: "story-social-b8-employee-support-actions",
          label: "What actions did you take to support employees this year?",
          placeholder: "e.g. Introduced flexible working hours, launched mental health support program, provided skills training...",
          rows: 3,
        },
      ],
    },
    {
      id: "b9",
      title: "2. B9 Health & Safety",
      description: "Describe your health & safety approach and main risks in your operations.",
      icon: HeartPulse,
      fields: [
        {
          key: "b9HealthSafetyApproach",
          id: "story-social-b9-health-safety-approach",
          label: "Describe your health & safety approach and main risks in your operations.",
          placeholder: "e.g. We follow ISO 45001 guidelines. Main risks include machinery operation, chemical handling, and manual lifting...",
          rows: 4,
        },
        {
          key: "b9PreventionMeasures",
          id: "story-social-b9-prevention-measures",
          label: "What measures are in place to prevent incidents?",
          placeholder: "e.g. Regular safety training, mandatory PPE, quarterly risk assessments, incident reporting system...",
          rows: 3,
        },
        {
          key: "b9IssuesImprovements",
          id: "story-social-b9-issues-improvements",
          label: "Any notable H&S issues or improvements this year?",
          placeholder: "e.g. Zero lost-time injuries recorded. Implemented new safety signage across all facilities...",
          rows: 3,
        },
      ],
    },
    {
      id: "b10",
      title: "3. B10 Remuneration/Training",
      description: "Describe your approach to fair pay and employee development/training.",
      icon: GraduationCap,
      fields: [
        {
          key: "b10FairPayTrainingApproach",
          id: "story-social-b10-fair-pay-training-approach",
          label: "Describe your approach to fair pay and employee development/training.",
          placeholder: "e.g. All employees paid above living wage. Annual performance reviews linked to salary increases. 40 hours/year training budget per employee...",
          rows: 4,
        },
        {
          key: "b10TrainingPriorities",
          id: "story-social-b10-training-priorities",
          label: "What training topics were prioritized this year and why?",
          placeholder: "e.g. 1) Digital skills — to support business digitization. 2) Leadership development — to build internal talent pipeline. 3) ESG awareness...",
          rows: 3,
        },
        {
          key: "b10NextYearImprovements",
          id: "story-social-b10-next-year-improvements",
          label: "What will you improve next year on pay/training?",
          placeholder: "e.g. Introduce gender pay gap analysis, expand apprenticeship program, add technical certifications for production staff...",
          rows: 3,
        },
      ],
    },
    {
      id: "c5",
      title: "4. C5 Additional Workforce & Diversity",
      description: "Describe any diversity & inclusion policies or practices you have in place (e.g., Women in leadership development program, Equal pay audit, inclusive hiring practices, Anti-discrimination policy).",
      icon: BadgeAlert,
      fields: [
        {
          key: "c5DiversityInclusionPolicies",
          id: "story-social-c5-diversity-inclusion-policies",
          label: "Describe any diversity & inclusion policies or practices you have in place (e.g., Women in leadership development program, Equal pay audit, inclusive hiring practices, Anti-discrimination policy).",
          placeholder: "e.g. We have an anti-discrimination policy, conduct annual equal pay audits, and run a women in leadership mentorship program...",
          rows: 4,
        },
        {
          key: "c5ContractorReliance",
          id: "story-social-c5-contractor-reliance",
          label: "Do you rely on contractors/temporary/outsourced workers? If yes: describe where and why.",
          placeholder: "e.g. Yes, we use contractors for seasonal peak demand (harvest/packaging) and specialized IT maintenance services...",
          rows: 3,
        },
        {
          key: "c5ContractorManagement",
          id: "story-social-c5-contractor-management",
          label: "How do you ensure contractors are managed safely and fairly?",
          placeholder: "e.g. All contractors must comply with our H&S policy, complete safety induction, and wages are monitored against minimum standards...",
          rows: 3,
        },
        {
          key: "c5DataLimitations",
          id: "story-social-c5-data-limitations",
          label: "Any limitations in visibility/data for additional workforce?",
          placeholder: "e.g. Limited data on contractor demographics and training hours. We plan to improve data collection through contractor portal in 2026...",
          rows: 3,
        },
      ],
    },
    {
      id: "c6",
      title: "5. C6 Human Rights Policies",
      description: "Describe your human rights approach (non-discrimination, child/forced labor, harassment, freedom of association, grievance).",
      icon: Scale,
      fields: [
        {
          key: "c6HumanRightsApproach",
          id: "story-social-c6-human-rights-approach",
          label: "Describe your human rights approach (non-discrimination, child/forced labor, harassment, freedom of association, grievance).",
          placeholder: "e.g. We have zero tolerance for discrimination, child/forced labor, and harassment. Employees can join unions. Anonymous grievance hotline available...",
          rows: 4,
        },
        {
          key: "c6HumanRightsRisks",
          id: "story-social-c6-human-rights-risks",
          label: "Where is the most relevant human-rights risks for your business (operations or suppliers)?",
          placeholder: "e.g. Primary risk is in our raw material supply chain (agriculture sector) where child labor risks exist. We audit all Tier 1 suppliers...",
          rows: 3,
        },
        {
          key: "c6ControlsInPlace",
          id: "story-social-c6-controls-in-place",
          label: "What controls are in place (policies, supplier checks, training, grievance channel)?",
          placeholder: "e.g. Supplier Code of Conduct signed by all suppliers, annual audits of high-risk suppliers, employee training on ethical sourcing, anonymous whistleblower hotline...",
          rows: 4,
        },
      ],
    },
  ];

  const showWorkforceIncidents = humanRightsData.workforceIncidents === true;
  const showValueChainIncidents = humanRightsData.valueChainIncidents === true;

  return (
    <div className="flex flex-col gap-6">
      <CardPanelHeader
        title={"Social"}
        description={"Workforce, health & safety, training, diversity, human rights."}
        onClose={onCollapse}
        onSave={() => {
          void forceSave();
        }}
        saveStatus={saveStatus}
        disabled={disabled}
        tag={"Sustainability Story"}
      />

      {disabled && <LockedBanner />}

      <section className="form-surface-card-lg flex flex-col gap-5 bg-white p-5 sm:p-6">
        <div className="rounded-[24px] border border-primary/10 bg-primary/5 px-4 py-4 sm:px-5">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <Info className="h-4 w-4" />
            </div>
            <p className="text-sm leading-6 text-slate-500">
              {"Fill in the"} <strong className="font-semibold text-slate-900">{"Social"}</strong>{" "}
              {"tab first — the data you enter there (workforce numbers, health & safety, human rights) provides the foundation for the narrative fields below."}
            </p>
          </div>
        </div>
      </section>

      {sections.map((section) => (
        <SectionCard
          key={section.id}
          header={
            <div className="flex flex-col gap-4 border-b border-slate-200 pb-6 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[20px] bg-primary/10 text-primary">
                  <section.icon className="h-5 w-5" />
                </div>
                <div className="space-y-1.5">
                  <h3 className="text-xl font-semibold leading-7 text-slate-900">{section.title}</h3>
                  <p className="max-w-4xl text-sm leading-6 text-slate-500">{section.description}</p>
                </div>
              </div>
            </div>
          }
        >
          <div className="grid gap-4">
            {section.fields.map((field) => (
              <VoiceTextareaField
                key={field.key}
                id={field.id}
                label={field.label}
                placeholder={field.placeholder}
                value={data[field.key]}
                rows={field.rows}
                disabled={disabled}
                onChange={(value) => update({ [field.key]: value })}
              />
            ))}
          </div>
        </SectionCard>
      ))}

      {(showWorkforceIncidents || showValueChainIncidents) && (
        <SectionCard
          header={
            <div className="flex flex-col gap-4 border-b border-slate-200 pb-6 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[20px] bg-primary/10 text-primary">
                  <BadgeAlert className="h-5 w-5" />
                </div>
                <div className="space-y-1.5">
                  <h3 className="text-xl font-semibold leading-7 text-slate-900">{"6. C7 Severe Incidents"}</h3>
                </div>
              </div>
            </div>
          }
        >
          <div className="grid gap-4">
            {showWorkforceIncidents ? (
              <VoiceTextareaField
                id="story-social-c7-workforce-incidents"
                label={"Describe the incident in your own workforce, when it occurred, who was involved, and what corrective actions were taken."}
                placeholder={"e.g. In Q2 2025, one employee suffered minor injury during forklift operation. Immediate first aid provided, incident investigated, additional safety training rolled out to all operators..."}
                value={data.c7WorkforceIncidentsNarrative}
                rows={4}
                disabled={disabled}
                onChange={(value) => update({ c7WorkforceIncidentsNarrative: value })}
              />
            ) : null}

            {showValueChainIncidents ? (
              <VoiceTextareaField
                id="story-social-c7-value-chain-incidents"
                label={"Describe incidents in suppliers, contractors, local communities, or consumer impact, and remedial actions taken."}
                placeholder={"e.g. One supplier was found to have wage violations. We required immediate remediation, conducted follow-up audit, and terminated contract as violations persisted..."}
                value={data.c7ValueChainIncidentsNarrative}
                rows={4}
                disabled={disabled}
                onChange={(value) => update({ c7ValueChainIncidentsNarrative: value })}
              />
            ) : null}
          </div>
        </SectionCard>
      )}

      <ProgressCard completion={storySocialCompletion} />
    </div>
  );
}
